
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

export type ResultLevel = "safe" | "suspicious" | "fraudulent";

interface AnalysisResultsProps {
  results: {
    type: string;
    level: ResultLevel;
    score: number;
    details: string;
  }[];
  overallStatus: ResultLevel;
  isLoading?: boolean;
}

const getStatusColor = (status: ResultLevel): string => {
  switch (status) {
    case "safe":
      return "bg-green-500/10 text-green-500 hover:bg-green-500/20";
    case "suspicious":
      return "bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20";
    case "fraudulent":
      return "bg-red-500/10 text-red-500 hover:bg-red-500/20";
  }
};

const getStatusIcon = (status: ResultLevel) => {
  switch (status) {
    case "safe":
      return <CheckCircle2 className="h-4 w-4" />;
    case "suspicious":
      return <AlertTriangle className="h-4 w-4" />;
    case "fraudulent":
      return <XCircle className="h-4 w-4" />;
  }
};

const getProgressColor = (score: number): string => {
  if (score >= 70) return "bg-red-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-green-500";
};

const AnalysisResults = ({ results, overallStatus, isLoading = false }: AnalysisResultsProps) => {
  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle>Analysis Results</CardTitle>
              <Badge className="bg-gray-200 text-gray-700">Processing...</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">AI Content Detection</span>
                  <span className="text-sm text-muted-foreground">Analyzing...</span>
                </div>
                <Progress value={0} />
                <p className="text-xs text-muted-foreground">Initial analysis in progress. Results will be available shortly.</p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Linguistic Analysis</span>
                  <span className="text-sm text-muted-foreground">Analyzing...</span>
                </div>
                <Progress value={0} />
                <p className="text-xs text-muted-foreground">Linguistic metrics unavailable.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle>Analysis Results</CardTitle>
            <Badge className={getStatusColor(overallStatus)}>
              <div className="flex items-center gap-1">
                {getStatusIcon(overallStatus)}
                <span className="capitalize">{overallStatus}</span>
              </div>
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {results.map((result, i) => (
              <div key={i} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{result.type}</span>
                  <span className="text-sm text-muted-foreground">{result.score}%</span>
                </div>
                <Progress 
                  value={result.score} 
                />
                <p className="text-xs text-muted-foreground">{result.details}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalysisResults;
